import {BehaviorSubject, Observable, Subject} from "rxjs";
import {EditorMode} from "../../view/editor-menu/editor-mode";
import {EventEmitter, Injectable, OnDestroy} from "@angular/core";
import {BaseData} from "../../models/basedata.model";
import {TrainrunDialogParameter} from "../../view/dialogs/trainrun-and-section-dialog/trainrun-and-section-dialog.component";
import {ThemeBase} from "../../view/themes/theme-base";
import {ConfirmationDialogParameter} from "../../view/dialogs/confirmation-dialog/confirmation-dialog.component";
import {FilterService} from "./filter.service";
import {NodeService} from "../data/node.service";
import {BaseDataService} from "../data/basedata.service";
import {InformSelectedTrainrunClick, TrainrunSectionService} from "../data/trainrunsection.service";
import {Vec2D} from "../../utils/vec2D";
import {FilterWindowType} from "../../view/filter-main-side-view/filter-main-side-view.component";
import {takeUntil} from "rxjs/operators";
import {ThemeFach} from "../../view/themes/theme-fach";
import {ThemeDefaultUxDark} from "../../view/themes/theme-default-ux-dark";
import {ThemeDefaultUx} from "../../view/themes/theme-default-ux";
import {ThemeGrayDark} from "../../view/themes/theme-gray-dark";
import {ThemeGray} from "../../view/themes/theme-gray";
import {ThemeFachDark} from "../../view/themes/theme-fach-dark";
import {ThemeFachPrint} from "../../view/themes/theme-fach-print";
import {ThemeRegistration} from "../../view/themes/theme-registration";
import {NoteService} from "../data/note.service";
import {NoteDialogParameter} from "../../view/dialogs/note-dialog/note-dialog.component";
import {StreckengrafikRenderingType} from "../../view/themes/streckengrafik-rendering-type";
import {NetzgrafikColoringService} from "../data/netzgrafikColoring.service";
import {MainViewMode} from "../../view/filter-main-side-view/main-view-mode";
import {TrainrunService} from "../data/trainrun.service";
import {Node} from "../../models/node.model";
import {LoadPerlenketteService} from "../../perlenkette/service/load-perlenkette.service";
import {TravelTimeCreationEstimatorType} from "../../view/themes/editor-trainrun-traveltime-creator-type";
import {OrderingAlgorithm} from "../../data-structures/technical.data.structures";
import {TrafficSide} from "src/app/data-structures/business.data.structures";
import {DataService} from "../data/data.service";
import {Operation, MetadataOperation} from "../../models/operation.model";
import {EditorView} from "src/app/view/editor-main-view/data-views/editor.view";

export interface ViewboxProperties {
  currentViewBox: string;
  panZoomTop: number;
  panZoomLeft: number;
  panZoomWidth: number;
  panZoomHeight: number;
  zoomFactor: number;
  origWidth: number;
  origHeight: number;
}

@Injectable({
  providedIn: "root",
})
export class UiInteractionService implements OnDestroy {
  updateNodeBaseDataSubject = new Subject<void>();
  readonly updateNodeBaseDataWindow = this.updateNodeBaseDataSubject.asObservable();

  showNodeBaseDataSubject = new Subject<boolean>();
  readonly nodeBaseDataWindow = this.showNodeBaseDataSubject.asObservable();

  showFilterSubject = new Subject<FilterWindowType | null>();
  readonly filterWindow = this.showFilterSubject.asObservable();

  showPerlenketteSubject = new Subject<boolean>();
  readonly perlenketteWindow = this.showPerlenketteSubject.asObservable();

  sendPerlenketteTrainrunSectionClicked = new Subject<number>();
  readonly perlenketteTrainrunSectionClicked =
    this.sendPerlenketteTrainrunSectionClicked.asObservable();

  printGraphikSubject = new Subject<null>();
  readonly printGraphik = this.printGraphikSubject.asObservable();

  showTrainrunDialogSubject = new Subject<TrainrunDialogParameter | null>();
  readonly trainrunDialog = this.showTrainrunDialogSubject.asObservable();

  showNoteDialogSubject = new Subject<NoteDialogParameter>();
  readonly noteDialog = this.showNoteDialogSubject.asObservable();

  showConfirmationDiagramDialogSubject = new Subject<ConfirmationDialogParameter>();
  readonly confirmationDiagramDialog = this.showConfirmationDiagramDialogSubject.asObservable();

  showBaseDataEditDialogSubject = new Subject<BaseData[]>();
  readonly baseDataEditDialog = this.showBaseDataEditDialogSubject.asObservable();
  readonly stammdatenEditDialog = this.baseDataEditDialog;

  zoomInSubject = new Subject<Vec2D>();
  readonly zoomInObservable = this.zoomInSubject.asObservable();

  zoomOutSubject = new Subject<Vec2D>();
  readonly zoomOutObservable = this.zoomOutSubject.asObservable();

  zoomResetSubject = new Subject<Vec2D>();
  readonly zoomResetObservable = this.zoomResetSubject.asObservable();

  zoomFactorSubject = new Subject<number>();
  readonly zoomFactorObservable = this.zoomFactorSubject.asObservable();

  setEditorModeSubject = new Subject<number>();
  readonly setEditorModeObservable = this.setEditorModeSubject.asObservable();

  showStreckengrafikSubject = new BehaviorSubject<MainViewMode>(MainViewMode.Netzgrafik);
  readonly streckengrafikWindow = this.showStreckengrafikSubject.asObservable();

  showOriginDestinationSubjet = new BehaviorSubject<MainViewMode>(MainViewMode.Netzgrafik);
  readonly originDestinationWindow = this.showOriginDestinationSubjet.asObservable();

  moveNetzgrafikEditorViewFocalPointSubject = new Subject<Vec2D>();
  readonly moveNetzgrafikEditorViewFocalPointObservable =
    this.moveNetzgrafikEditorViewFocalPointSubject.asObservable();

  readonly operation = new EventEmitter<Operation>();

  private activeTheme: ThemeBase = null;
  private activeStreckengrafikRenderingType: StreckengrafikRenderingType = null;
  private activeTravelTimeCreationEstimatorType: TravelTimeCreationEstimatorType = null;
  private editorMode: EditorMode = EditorMode.NetzgrafikEditing;
  private isMultiSelectedNodesCorridor = false;

  private windowViewboxPropertiesMap: {[key: string]: ViewboxProperties} = {};
  private destroyed = new Subject<void>();
  private filterWindowType: FilterWindowType | null = null;

  constructor(
    private filterService: FilterService,
    private nodeService: NodeService,
    private noteService: NoteService,
    private baseDataService: BaseDataService,
    private trainrunSectionService: TrainrunSectionService,
    private trainrunService: TrainrunService,
    private netzgrafikColoringService: NetzgrafikColoringService,
    private loadPerlenketteService: LoadPerlenketteService,
    private dataService: DataService,
  ) {
    this.activeTheme = null;
    this.loadActiveTheme();

    // listen for browser setting update
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
      this.activeTheme = null;
      this.loadActiveTheme();
    });

    this.activeStreckengrafikRenderingType = null;
    this.activeTravelTimeCreationEstimatorType = null;

    this.filterService.filter.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.nodeService.nodesUpdated();
      this.nodeService.transitionsUpdated();
      this.nodeService.connectionsUpdated();
      this.noteService.notesUpdated();
      this.trainrunSectionService.trainrunSectionsUpdated();
      this.handlePerlenketteOnFilterChanged();
    });
    this.trainrunSectionService.informSelectedTrainrunClick
      .pipe(takeUntil(this.destroyed))
      .subscribe((informSelectedTrainrunClick: InformSelectedTrainrunClick) => {
        this.showPerlenkette(informSelectedTrainrunClick);
      });

    this.trainrunService.trainruns.pipe(takeUntil(this.destroyed)).subscribe(() => {
      const st = trainrunService.getSelectedTrainrun();
      if (st !== null) {
        return;
      }
      this.closePerlenkette();
    });

    this.baseDataService.baseDataObservable
      .pipe(takeUntil(this.destroyed))
      .subscribe((baseData: BaseData[]) => {
        this.updateNodeBaseData();
        this.showBaseDataEditDialog(baseData);
      });
    this.filterWindow.pipe(takeUntil(this.destroyed)).subscribe((type: FilterWindowType | null) => {
      this.filterWindowType = type;
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  getViewboxProperties(key: string): ViewboxProperties {
    const viewboxProperties = this.windowViewboxPropertiesMap[key];
    if (viewboxProperties === undefined) {
      return this.getDefaultViewProperties();
    }
    return viewboxProperties;
  }

  getDefaultViewProperties(): ViewboxProperties {
    return {
      currentViewBox: null,
      panZoomTop: 0,
      panZoomLeft: 0,
      panZoomWidth: 0,
      panZoomHeight: 0,
      zoomFactor: 100,
      origWidth: 0,
      origHeight: 0,
    };
  }

  setViewboxProperties(key: string, viewboxProperties: ViewboxProperties) {
    this.windowViewboxPropertiesMap[key] = Object.assign({}, viewboxProperties);
  }

  isLocalStoredColorTheme() {
    const serializedState = localStorage.getItem("UiInteractionService");
    if (
      serializedState === null ||
      serializedState === undefined ||
      serializedState === "undefined"
    ) {
      return false;
    }
    return true;
  }

  resetLocalStoredColorData() {
    localStorage.removeItem("UiInteractionService");
    this.activeTheme = null;
    this.loadActiveTheme();
  }

  loadActiveTheme() {
    this.loadUserSettingFromLocalStorage();
    if (this.activeTheme === null) {
      // detect at initialization
      if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        this.setActiveTheme(new ThemeFachDark(), false);
      } else {
        this.setActiveTheme(new ThemeFach(), false);
      }
      this.netzgrafikColoringService.setDarkMode(this.activeTheme.isDark);
    }
    if (this.activeStreckengrafikRenderingType === null) {
      this.activeStreckengrafikRenderingType = StreckengrafikRenderingType.UniformDistance;
    }
    if (this.activeTravelTimeCreationEstimatorType === null) {
      this.activeTravelTimeCreationEstimatorType = TravelTimeCreationEstimatorType.RetrieveFromEdge;
    }
  }

  createTheme(
    themeRegistration: ThemeRegistration,
    activeBackgroundColor: string,
    activeDarkBackgroundColor: string,
  ) {
    let activeTheme: ThemeBase;
    if (themeRegistration === ThemeRegistration.ThemeDefaultUxDark) {
      activeTheme = new ThemeDefaultUxDark(activeDarkBackgroundColor, activeDarkBackgroundColor);
    } else if (themeRegistration === ThemeRegistration.ThemeDefaultUx) {
      activeTheme = new ThemeDefaultUx(activeBackgroundColor, activeBackgroundColor);
    } else if (themeRegistration === ThemeRegistration.ThemeGrayDark) {
      activeTheme = new ThemeGrayDark(activeDarkBackgroundColor, activeDarkBackgroundColor);
    } else if (themeRegistration === ThemeRegistration.ThemeGray) {
      activeTheme = new ThemeGray(activeBackgroundColor, activeBackgroundColor);
    } else if (themeRegistration === ThemeRegistration.ThemeFachPrint) {
      activeTheme = new ThemeFachPrint(activeBackgroundColor, activeBackgroundColor);
    } else if (themeRegistration === ThemeRegistration.ThemeFachDark) {
      activeTheme = new ThemeFachDark(activeDarkBackgroundColor, activeDarkBackgroundColor);
    } else {
      activeTheme = new ThemeFach(activeBackgroundColor, activeBackgroundColor);
    }
    this.setActiveTheme(activeTheme);
  }

  getActiveTheme(): ThemeBase {
    return this.activeTheme;
  }

  getActiveStreckengrafikRenderingType(): StreckengrafikRenderingType {
    return this.activeStreckengrafikRenderingType;
  }

  setActiveStreckengrafikRenderingType(streckengrafikRenderingType: StreckengrafikRenderingType) {
    if (streckengrafikRenderingType === undefined) {
      streckengrafikRenderingType = StreckengrafikRenderingType.UniformDistance;
    }

    this.activeStreckengrafikRenderingType = streckengrafikRenderingType;
    this.saveUserSettingToLocalStorage();
    this.trainrunSectionService.trainrunSectionsUpdated();
  }

  getActiveTravelTimeCreationEstimatorType(): TravelTimeCreationEstimatorType {
    return this.activeTravelTimeCreationEstimatorType;
  }

  setActiveTravelTimeCreationEstimatorType(
    activeTravelTimeCreationEstimatorType: TravelTimeCreationEstimatorType,
  ) {
    if (activeTravelTimeCreationEstimatorType === undefined) {
      activeTravelTimeCreationEstimatorType = TravelTimeCreationEstimatorType.Fixed;
    }
    this.activeTravelTimeCreationEstimatorType = activeTravelTimeCreationEstimatorType;
    this.saveUserSettingToLocalStorage();
  }

  getActiveTrafficSideType(): TrafficSide {
    return this.dataService.getTrafficSide();
  }

  setActiveTrafficSideType(activeTrafficSideType: TrafficSide) {
    this.dataService.setTrafficSide(activeTrafficSideType);
    this.trainrunSectionService.updateText();
    this.trainrunSectionService.trainrunSectionsUpdated();
    this.operation.emit(new MetadataOperation({trafficSide: activeTrafficSideType}));
  }

  getActiveOrderingAlgorithm(): OrderingAlgorithm {
    return this.nodeService.getCurrentOrderingAlgorithm();
  }

  setActiveOrderingAlgorithm(orderingAlgorithm: OrderingAlgorithm) {
    if (orderingAlgorithm === undefined) {
      orderingAlgorithm = OrderingAlgorithm.Alphabetical;
    }
    this.nodeService.initPortOrdering(orderingAlgorithm);
    this.nodeService.nodesUpdated();
    this.nodeService.transitionsUpdated();
    this.nodeService.connectionsUpdated();
    this.trainrunSectionService.trainrunSectionsUpdated();
    this.operation.emit(new MetadataOperation({orderingAlgorithm}));
  }

  updateNodeBaseData() {
    this.updateNodeBaseDataSubject.next();
  }

  showNodeBaseData() {
    this.updateNodeBaseDataSubject.next();
    this.showNodeBaseDataSubject.next(true);
  }

  closeNodeBaseData() {
    this.showNodeBaseDataSubject.next(false);
  }

  showNetzgrafik() {
    this.showStreckengrafikSubject.next(MainViewMode.Netzgrafik);
  }

  showStreckengrafik() {
    this.showStreckengrafikSubject.next(MainViewMode.Streckengrafik);
  }

  showOriginDestination() {
    this.showOriginDestinationSubjet.next(MainViewMode.OriginDestination);
  }

  moveNetzgrafikEditorFocalViewPoint(center: Vec2D) {
    this.moveNetzgrafikEditorViewFocalPointSubject.next(center);
  }

  getNetzgrafikBoundingBox(): {
    minCoordX: number;
    minCoordY: number;
    maxCoordX: number;
    maxCoordY: number;
  } {
    const rects = [
      ...this.nodeService.getVisibleNodes().map((node) => ({
        x: node.getPositionX(),
        y: node.getPositionY(),
        w: node.getNodeWidth(),
        h: node.getNodeHeight(),
      })),
      ...this.noteService.getVisibleNotes().map((note) => ({
        x: note.getPositionX(),
        y: note.getPositionY(),
        w: note.getWidth(),
        h: note.getHeight(),
      })),
    ];

    if (rects.length === 0) {
      return {minCoordX: 0, minCoordY: 0, maxCoordX: 0, maxCoordY: 0};
    }

    return {
      minCoordX: Math.min(...rects.map((r) => r.x)),
      minCoordY: Math.min(...rects.map((r) => r.y)),
      maxCoordX: Math.max(...rects.map((r) => r.x + r.w)),
      maxCoordY: Math.max(...rects.map((r) => r.y + r.h)),
    };
  }

  viewportCenteringOnNodesBoundingBox() {
    const bb = this.getNetzgrafikBoundingBox();
    this.moveNetzgrafikEditorFocalViewPoint(
      new Vec2D((bb.minCoordX + bb.maxCoordX) / 2.0, (bb.minCoordY + bb.maxCoordY) / 2.0),
    );
  }

  findClosestNodeToViewCenter(nodes: Node[]): {node: Node | undefined; offset: Vec2D} {
    const vb = this.getViewboxProperties(EditorView.svgName);
    // Compute center of the current viewBox
    const center = new Vec2D(
      vb.panZoomLeft + vb.panZoomWidth / 2,
      vb.panZoomTop + vb.panZoomHeight / 2,
    );

    let minDistance = Number.MAX_VALUE;
    let closest: Node | undefined = undefined;
    let offset: Vec2D = new Vec2D(0, 0);

    // Search for the node closest to the center of the viewBox
    for (const n of nodes) {
      const nodePos = new Vec2D(
        n.getPositionX() + n.getNodeWidth() / 2.0,
        n.getPositionY() + n.getNodeHeight() / 2.0,
      );
      const dist = Vec2D.norm(Vec2D.sub(nodePos, center));

      if (dist < minDistance) {
        minDistance = dist;
        closest = n;
      }
    }

    if (closest) {
      offset = Vec2D.sub(
        center,
        new Vec2D(
          closest.getPositionX() + closest.getNodeWidth() / 2.0,
          closest.getPositionY() + closest.getNodeHeight() / 2.0,
        ),
      );
    }

    return {node: closest, offset: offset};
  }

  gotoNode(node: Node, offset: Vec2D = new Vec2D(0, 0)) {
    // Move the center of the view to the center of the node, applying an optional offset
    const x = node.getPositionX() + node.getNodeWidth() / 2.0 + offset.getX();
    const y = node.getPositionY() + node.getNodeHeight() / 2.0 + offset.getY();
    this.moveNetzgrafikEditorFocalViewPoint(new Vec2D(x, y));
  }

  showOrCloseFilter(type: FilterWindowType) {
    if (this.isFilterWindowType(type)) {
      this.closeFilter();
      return;
    }
    this.showFilterSubject.next(type);
  }

  isFilterWindowType(type: FilterWindowType): boolean {
    return this.filterWindowType === type;
  }

  closeFilter() {
    this.showFilterSubject.next(null);
  }

  showPerlenkette(informSelectedTrainrunClick: InformSelectedTrainrunClick) {
    this.nodeService.unselectAllConnections();
    if (informSelectedTrainrunClick.open) {
      this.showPerlenketteSubject.next(true);
    }
    this.sendPerlenketteTrainrunSectionClicked.next(informSelectedTrainrunClick.trainrunSectionId);
  }

  closePerlenkette() {
    this.nodeService.unselectAllConnections();
    this.showPerlenketteSubject.next(false);
  }

  closeTrainrunDialog() {
    this.showTrainrunDialogSubject.next(null);
  }

  print() {
    this.printGraphikSubject.next(null);
  }

  showTrainrunDialog(parameter: TrainrunDialogParameter) {
    this.showTrainrunDialogSubject.next(parameter);
  }

  showConfirmationDiagramDialog(
    confirmationDiagramParameter: ConfirmationDialogParameter,
  ): Observable<boolean> {
    this.showConfirmationDiagramDialogSubject.next(confirmationDiagramParameter);
    return confirmationDiagramParameter.dialogFeedback;
  }

  showBaseDataEditDialog(baseData: BaseData[]) {
    this.showBaseDataEditDialogSubject.next(baseData);
  }

  zoomIn(zoomCenter: Vec2D) {
    this.zoomInSubject.next(zoomCenter);
  }

  zoomOut(zoomCenter: Vec2D) {
    this.zoomOutSubject.next(zoomCenter);
  }

  zoomReset(zoomCenter: Vec2D) {
    this.zoomResetSubject.next(zoomCenter);
  }

  zoomFactorChanged(zoomFactor: number) {
    this.zoomFactorSubject.next(zoomFactor);
  }

  setEditorMode(mode: EditorMode) {
    this.editorMode = mode;
    this.setEditorModeSubject.next(this.editorMode);
  }

  getEditorMode(): EditorMode {
    return this.editorMode;
  }

  resetEditorMode() {
    this.editorMode = EditorMode.NetzgrafikEditing;
    this.setEditorModeSubject.next(this.editorMode);
  }

  disableMultiSelectedNodesCorridor() {
    this.isMultiSelectedNodesCorridor = false;
  }

  enableMultiSelectedNodesCorridor() {
    this.isMultiSelectedNodesCorridor = true;
  }

  isMultiSelectedNodesCorridorEnabled() {
    return this.isMultiSelectedNodesCorridor;
  }

  private setActiveTheme(theme: ThemeBase, updateLocalStorage = true) {
    this.activeTheme = theme;
    this.netzgrafikColoringService.setDarkMode(this.activeTheme.isDark);
    if (updateLocalStorage) {
      this.saveUserSettingToLocalStorage();
    }
    this.updateLightDark();
  }

  updateLightDark() {
    const el = document.getElementById("NetzgrafikRootHtml");
    if (el) {
      el.className = "sbb-lean" + (this.getActiveTheme().isDark ? " sbb-dark" : " sbb-light");
    }
  }

  private loadUserSettingFromLocalStorage() {
    try {
      const serializedState = localStorage.getItem("UiInteractionService");
      if (
        serializedState === null ||
        serializedState === undefined ||
        serializedState === "undefined"
      ) {
        return;
      }
      const localStoredInfo = JSON.parse(serializedState);
      const activeTheme = localStoredInfo.activeTheme;
      this.createTheme(
        activeTheme.themeRegistration,
        activeTheme.backgroundColor,
        activeTheme.backgroundStreckengraphikColor,
      );
      this.setActiveStreckengrafikRenderingType(localStoredInfo.streckengrafikRenderingType);
      this.setActiveTravelTimeCreationEstimatorType(
        localStoredInfo.travelTimeCreationEstimatorType,
      );
    } catch (err) {
      console.error(err);
    }
  }

  private saveUserSettingToLocalStorage() {
    try {
      localStorage.setItem(
        "UiInteractionService",
        JSON.stringify({
          activeTheme: this.getActiveTheme(),
          streckengrafikRenderingType: this.getActiveStreckengrafikRenderingType(),
          travelTimeCreationEstimatorType: this.getActiveTravelTimeCreationEstimatorType(),
        }),
      );
    } catch (err) {
      console.error(err);
    }
  }

  private handlePerlenketteOnFilterChanged() {
    if (
      this.loadPerlenketteService.getSelectedTrainrun() &&
      !this.filterService.filterTrainrun(this.loadPerlenketteService.getSelectedTrainrun())
    ) {
      this.closePerlenkette();
    }
  }
}
